import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../config/api';
import {
  ArrowLeft,
  CircleNotch,
  Plus,
  Minus,
  ArrowsClockwise,
  Package,
  Warning
} from '@phosphor-icons/react';


const CATEGORY_OPTIONS = [
  'Cámaras',
  'DVR/NVR',
  'Control de Acceso',
  'Alarmas',
  'Cables',
  'Conectores',
  'Fuentes de Poder',
  'Herramientas',
  'Accesorios',
  'Otro'
];

const UNIT_OPTIONS = [
  { value: 'unit', label: 'Unidad' },
  { value: 'meter', label: 'Metro' },
  { value: 'feet', label: 'Pie' },
  { value: 'box', label: 'Caja' },
  { value: 'roll', label: 'Rollo' },
];

export default function InventoryDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = id === 'new';

  const [item, setItem] = useState({
    name: '',
    sku: '',
    category: 'Cámaras',
    description: '',
    unit: 'unit',
    quantity: 0,
    min_stock_level: 5,
    cost_price: 0,
    sell_price: 0,
    location: '',
    supplier: '',
    track_serials: false,
    serial_numbers: []
  });
  const [movements, setMovements] = useState([]);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(isNew);
  const [showMovementModal, setShowMovementModal] = useState(false);
  const [movementData, setMovementData] = useState({ type: 'in', quantity: 1, notes: '' });

  useEffect(() => {
    if (!isNew) {
      fetchItem();
      fetchMovements();
    }
  }, [id, isNew]);

  const fetchItem = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/inventory/${id}`, { withCredentials: true });
      setItem(response.data);
    } catch (error) {
      console.error('Error fetching item:', error);
      navigate('/inventory');
    } finally {
      setLoading(false);
    }
  };

  const fetchMovements = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/inventory/${id}/movements`, { withCredentials: true });
      setMovements(response.data);
    } catch (error) {
      console.error('Error fetching movements:', error);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!item.name || !item.sku) {
      alert('Completa los campos requeridos');
      return;
    }

    setSaving(true);
    try {
      if (isNew) {
        const response = await axios.post(`${API_URL}/api/inventory`, item, { withCredentials: true });
        navigate(`/inventory/${response.data.id}`);
      } else {
        await axios.put(`${API_URL}/api/inventory/${id}`, item, { withCredentials: true });
        setEditing(false);
        fetchItem();
      }
    } catch (error) {
      console.error('Error saving item:', error);
      alert(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleMovement = async () => {
    try {
      await axios.post(`${API_URL}/api/inventory/movement`, {
        item_id: id,
        movement_type: movementData.type,
        quantity: movementData.quantity,
        notes: movementData.notes
      }, { withCredentials: true });
      
      setShowMovementModal(false);
      setMovementData({ type: 'in', quantity: 1, notes: '' });
      fetchItem();
      fetchMovements();
    } catch (error) {
      console.error('Error recording movement:', error);
      alert(error.response?.data?.detail || 'Error al registrar movimiento');
    }
  };

  const isLowStock = item.quantity <= item.min_stock_level;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <CircleNotch size={40} className="animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="inventory-detail-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/inventory')}
          className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
        >
          <ArrowLeft size={24} />
        </button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-slate-900">
            {isNew ? 'Nuevo Producto' : editing ? 'Editar Producto' : item.name}
          </h1>
          {!isNew && !editing && (
            <p className="text-slate-500">SKU: {item.sku}</p>
          )}
        </div>
        {!isNew && !editing && (
          <div className="flex items-center gap-2">
            <button
              onClick={() => setEditing(true)}
              className="px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200"
            >
              Editar
            </button>
            <button
              onClick={() => setShowMovementModal(true)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              data-testid="movement-button"
            >
              <ArrowsClockwise size={18} />
              Movimiento
            </button>
          </div>
        )}
      </div>

      {/* Low Stock Alert */}
      {!isNew && !editing && isLowStock && (
        <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 flex items-center gap-3">
          <Warning size={24} className="text-orange-500" />
          <div>
            <p className="font-medium text-orange-800">Stock bajo</p>
            <p className="text-sm text-orange-600">
              Quedan {item.quantity} {UNIT_OPTIONS.find(u => u.value === item.unit)?.label || item.unit}(s). 
              Mínimo recomendado: {item.min_stock_level}
            </p>
          </div>
        </div>
      )}

      {editing ? (
        /* Edit Form */
        <form onSubmit={handleSave} className="space-y-6">
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <h2 className="text-lg font-semibold mb-4">Información del Producto</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="form-label">Nombre *</label>
                <input
                  type="text"
                  value={item.name}
                  onChange={(e) => setItem({ ...item, name: e.target.value })}
                  className="form-input"
                  required
                  data-testid="name-input"
                />
              </div>
              <div>
                <label className="form-label">SKU *</label>
                <input
                  type="text"
                  value={item.sku}
                  onChange={(e) => setItem({ ...item, sku: e.target.value.toUpperCase() })}
                  className="form-input font-mono"
                  required
                  data-testid="sku-input"
                />
              </div>
              <div>
                <label className="form-label">Categoría</label>
                <select
                  value={item.category}
                  onChange={(e) => setItem({ ...item, category: e.target.value })}
                  className="form-input"
                >
                  {CATEGORY_OPTIONS.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="form-label">Unidad</label>
                <select
                  value={item.unit}
                  onChange={(e) => setItem({ ...item, unit: e.target.value })}
                  className="form-input"
                >
                  {UNIT_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="form-label">Descripción</label>
                <textarea
                  value={item.description || ''}
                  onChange={(e) => setItem({ ...item, description: e.target.value })}
                  className="form-input"
                  rows={2}
                />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <h2 className="text-lg font-semibold mb-4">Stock y Precios</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="form-label">Cantidad Inicial</label>
                <input
                  type="number"
                  value={item.quantity}
                  onChange={(e) => setItem({ ...item, quantity: parseFloat(e.target.value) || 0 })}
                  className="form-input"
                  min="0"
                  disabled={!isNew}
                />
              </div>
              <div>
                <label className="form-label">Stock Mínimo</label>
                <input
                  type="number"
                  value={item.min_stock_level}
                  onChange={(e) => setItem({ ...item, min_stock_level: parseFloat(e.target.value) || 0 })}
                  className="form-input"
                  min="0"
                />
              </div>
              <div>
                <label className="form-label">Precio de Costo ($)</label>
                <input
                  type="number"
                  value={item.cost_price}
                  onChange={(e) => setItem({ ...item, cost_price: parseFloat(e.target.value) || 0 })}
                  className="form-input"
                  min="0"
                  step="0.01"
                />
              </div>
              <div>
                <label className="form-label">Precio de Venta ($)</label>
                <input
                  type="number"
                  value={item.sell_price}
                  onChange={(e) => setItem({ ...item, sell_price: parseFloat(e.target.value) || 0 })}
                  className="form-input"
                  min="0"
                  step="0.01"
                />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <h2 className="text-lg font-semibold mb-4">Información Adicional</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="form-label">Ubicación</label>
                <input
                  type="text"
                  value={item.location || ''}
                  onChange={(e) => setItem({ ...item, location: e.target.value })}
                  className="form-input"
                  placeholder="ej: Estante A-3"
                />
              </div>
              <div>
                <label className="form-label">Proveedor</label>
                <input
                  type="text"
                  value={item.supplier || ''}
                  onChange={(e) => setItem({ ...item, supplier: e.target.value })}
                  className="form-input"
                />
              </div>
              <div className="md:col-span-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={item.track_serials}
                    onChange={(e) => setItem({ ...item, track_serials: e.target.checked })}
                    className="w-4 h-4 text-blue-600"
                  />
                  <span>Rastrear números de serie</span>
                </label>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={() => isNew ? navigate('/inventory') : setEditing(false)}
              className="px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={saving}
              className="inline-flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              data-testid="save-button"
            >
              {saving && <CircleNotch size={18} className="animate-spin" />}
              {saving ? 'Guardando...' : 'Guardar'}
            </button>
          </div>
        </form>
      ) : (
        /* View Mode */
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Info */}
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <div className="flex items-start gap-4 mb-6">
                <div className={`p-4 rounded-xl ${isLowStock ? 'bg-orange-100' : 'bg-blue-100'}`}>
                  <Package size={32} className={isLowStock ? 'text-orange-600' : 'text-blue-600'} />
                </div>
                <div>
                  <h2 className="text-xl font-bold">{item.name}</h2>
                  <p className="text-slate-500">SKU: {item.sku}</p>
                  <span className="inline-block mt-2 px-3 py-1 bg-slate-100 text-slate-700 rounded-full text-sm">
                    {item.category}
                  </span>
                </div>
              </div>
              
              {item.description && (
                <p className="text-slate-600 mb-6">{item.description}</p>
              )}

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-500">Cantidad</p>
                  <p className={`text-2xl font-bold ${isLowStock ? 'text-orange-600' : 'text-slate-900'}`}>
                    {item.quantity}
                  </p>
                  <p className="text-xs text-slate-400">Mín: {item.min_stock_level}</p>
                </div>
                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-500">Unidad</p>
                  <p className="text-lg font-semibold">
                    {UNIT_OPTIONS.find(u => u.value === item.unit)?.label || item.unit}
                  </p>
                </div>
                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-500">Costo</p>
                  <p className="text-lg font-semibold">${item.cost_price?.toLocaleString()}</p>
                </div>
                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-500">Precio Venta</p>
                  <p className="text-lg font-semibold text-green-600">${item.sell_price?.toLocaleString()}</p>
                </div>
              </div>
            </div>

            {/* Movements History */}
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-slate-200">
                <h3 className="font-semibold">Historial de Movimientos</h3>
              </div>
              {movements.length > 0 ? (
                <div className="divide-y divide-slate-100">
                  {movements.map((mov) => (
                    <div key={mov.id} className="px-6 py-4 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-full ${
                          mov.movement_type === 'in' ? 'bg-green-100' : 
                          mov.movement_type === 'out' ? 'bg-red-100' : 'bg-blue-100'
                        }`}>
                          {mov.movement_type === 'in' ? (
                            <Plus size={16} className="text-green-600" />
                          ) : mov.movement_type === 'out' ? (
                            <Minus size={16} className="text-red-600" />
                          ) : (
                            <ArrowsClockwise size={16} className="text-blue-600" />
                          )}
                        </div>
                        <div>
                          <p className="font-medium">
                            {mov.movement_type === 'in' ? 'Entrada' : 
                             mov.movement_type === 'out' ? 'Salida' : 'Ajuste'}
                          </p>
                          <p className="text-sm text-slate-500">
                            {new Date(mov.created_at).toLocaleString('es-ES')}
                          </p>
                          {mov.notes && <p className="text-sm text-slate-500">{mov.notes}</p>}
                        </div>
                      </div>
                      <div className="text-right">
                        <p className={`font-bold ${
                          mov.movement_type === 'in' ? 'text-green-600' : 
                          mov.movement_type === 'out' ? 'text-red-600' : 'text-blue-600'
                        }`}>
                          {mov.movement_type === 'in' ? '+' : mov.movement_type === 'out' ? '-' : '='}{mov.quantity}
                        </p>
                        <p className="text-xs text-slate-400">{mov.previous_qty} → {mov.new_qty}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="px-6 py-8 text-center text-slate-500">
                  No hay movimientos registrados
                </div>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h3 className="font-semibold mb-4">Información Adicional</h3>
              <div className="space-y-3">
                {item.location && (
                  <div>
                    <p className="text-sm text-slate-500">Ubicación</p>
                    <p className="font-medium">{item.location}</p>
                  </div>
                )}
                {item.supplier && (
                  <div>
                    <p className="text-sm text-slate-500">Proveedor</p>
                    <p className="font-medium">{item.supplier}</p>
                  </div>
                )}
                <div>
                  <p className="text-sm text-slate-500">Valor en Stock</p>
                  <p className="font-medium text-lg">
                    ${(item.quantity * item.cost_price).toLocaleString()}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Movement Modal */}
      {showMovementModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Registrar Movimiento</h3>
            <div className="space-y-4">
              <div>
                <label className="form-label">Tipo de Movimiento</label>
                <select
                  value={movementData.type}
                  onChange={(e) => setMovementData({ ...movementData, type: e.target.value })}
                  className="form-input"
                >
                  <option value="in">Entrada (Compra/Recepción)</option>
                  <option value="out">Salida (Venta/Uso)</option>
                  <option value="adjustment">Ajuste de Inventario</option>
                </select>
              </div>
              <div>
                <label className="form-label">Cantidad</label>
                <input
                  type="number"
                  value={movementData.quantity}
                  onChange={(e) => setMovementData({ ...movementData, quantity: parseFloat(e.target.value) || 0 })}
                  className="form-input"
                  min="1"
                />
              </div>
              <div>
                <label className="form-label">Notas</label>
                <input
                  type="text"
                  value={movementData.notes}
                  onChange={(e) => setMovementData({ ...movementData, notes: e.target.value })}
                  className="form-input"
                  placeholder="Opcional"
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowMovementModal(false)}
                className="px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200"
              >
                Cancelar
              </button>
              <button
                onClick={handleMovement}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Registrar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
